import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  Search,
  ExternalLink,
  MoreHorizontal,
  Pencil,
  Power,
  PowerOff,
  Trash2,
  Copy,
  Layers,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useNotifications } from '@/hooks/useNotifications';
import {
  useMerchantCategories,
  useMerchants,
  useUpdateMerchantStatus,
  useDeleteMerchant,
} from '@/hooks/useVendors';
import { useStates, useCities } from '@/hooks/useLocation';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { Merchant } from '@/api/vendors';
import { SearchableSelect } from '@/components/common/SearchableSelect';
import { VendorFormDialog } from './VendorFormDialog';
import { VendorReferralConfigModal } from './VendorReferralConfigModal';

const ALL_VALUE = 'all';

function truncateMiddle(text: string, start = 8, end = 6) {
  if (text.length <= start + end + 3) return text;
  return `${text.slice(0, start)}.....${text.slice(-end)}`;
}

function VendorActions({
  vendor,
  onEdit,
  onViewReferralConfig,
}: {
  vendor: Merchant;
  onEdit: () => void;
  onViewReferralConfig: () => void;
}) {
  const { success, error: showError } = useNotifications();
  const { mutate: updateStatus, isPending: isUpdating } = useUpdateMerchantStatus();
  const { mutate: deleteMerchant, isPending: isDeleting } = useDeleteMerchant();
  const isActive = vendor.status?.toLowerCase() === 'active';

  const handleToggleStatus = () => {
    const nextStatus = isActive ? 'Inactive' : 'Active';
    updateStatus(
      { merchantId: vendor.id, status: nextStatus },
      {
        onSuccess: () => success(`Vendor marked ${nextStatus.toLowerCase()}`),
        onError: () => showError('Failed to update vendor status'),
      }
    );
  };

  const handleDelete = () => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete vendor "${vendor.name}"?`
    );
    if (confirmDelete) {
      deleteMerchant(vendor.id, {
        onSuccess: () => success('Vendor deleted successfully'),
        onError: (err) => {
          const errMsg = err.response?.data?.message || 'Failed to delete vendor';
          showError(errMsg);
        },
      });
    }
  };

  const isPending = isUpdating || isDeleting;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 w-8 p-0" disabled={isPending}>
          <span className="sr-only">Open menu</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onEdit}>
          <Pencil className="mr-2 h-4 w-4" />
          Edit
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onViewReferralConfig}>
          <Layers className="mr-2 h-4 w-4" />
          Referral Config
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleToggleStatus} disabled={isPending}>
          {isActive ? <PowerOff className="mr-2 h-4 w-4" /> : <Power className="mr-2 h-4 w-4" />}
          {isActive ? 'Mark Inactive' : 'Mark Active'}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={handleDelete}
          disabled={isPending}
          className="text-destructive focus:text-destructive"
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function VendorsPage() {
  const navigate = useNavigate();
  const { success } = useNotifications();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(7);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [stateSearch, setStateSearch] = useState('');
  const [selectedStateCode, setSelectedStateCode] = useState('');
  const [state, setState] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [city, setCity] = useState('');
  const [industryCategory, setIndustryCategory] = useState<string>(ALL_VALUE);
  const [isFeatured, setIsFeatured] = useState<string>(ALL_VALUE);
  const [formState, setFormState] = useState<{ open: boolean; vendor?: Merchant }>({ open: false });
  const [referralModalVendor, setReferralModalVendor] = useState<Merchant | null>(null);

  const handleCopyEmail = (email: string) => {
    navigator.clipboard.writeText(email);
    success('Email address copied to clipboard');
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  const debouncedStateSearch = useDebouncedValue(stateSearch);
  const debouncedCitySearch = useDebouncedValue(citySearch);
  const { data: states, isLoading: isLoadingStates } = useStates(debouncedStateSearch);
  const { data: cities, isLoading: isLoadingCities } = useCities(
    selectedStateCode,
    debouncedCitySearch
  );
  const stateOptions = (states ?? []).map((s) => ({ value: s.code, label: s.name }));
  const cityOptions = (cities ?? []).map((c) => ({ value: c.name, label: c.name }));

  const handleSelectState = (option: { value: string; label: string }) => {
    setSelectedStateCode(option.value);
    setStateSearch(option.label);
    setState(option.label);
    setCitySearch('');
    setCity('');
    setPage(0);
  };

  const handleStateSearchChange = (value: string) => {
    setStateSearch(value);
    if (!value) {
      setSelectedStateCode('');
      setState('');
      setCitySearch('');
      setCity('');
      setPage(0);
    }
  };

  const handleSelectCity = (option: { value: string; label: string }) => {
    setCitySearch(option.label);
    setCity(option.value);
    setPage(0);
  };

  const handleCitySearchChange = (value: string) => {
    setCitySearch(value);
    if (!value) {
      setCity('');
      setPage(0);
    }
  };

  const { data: categories } = useMerchantCategories();
  const { data, isLoading, isFetching } = useMerchants({
    skip: page * pageSize,
    take: pageSize,
    search: search || undefined,
    city: city || undefined,
    state: state || undefined,
    industryCategory: industryCategory !== ALL_VALUE ? industryCategory : undefined,
    isFeatured: isFeatured !== ALL_VALUE ? isFeatured === 'true' : undefined,
  });

  const vendors = data?.data ?? [];
  const total = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Merchants</h2>
          <p className="text-muted-foreground">Manage merchant partners and their storefronts.</p>
        </div>
        <Button onClick={() => setFormState({ open: true })}>Add Merchant</Button>
      </div>

      {/* Search & filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1 sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search Merchants..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="sm:max-w-[180px]">
          <SearchableSelect
            searchTerm={stateSearch}
            onSearchTermChange={handleStateSearchChange}
            options={stateOptions}
            onSelect={handleSelectState}
            placeholder="Search state..."
            isLoading={isLoadingStates}
          />
        </div>
        <div className="sm:max-w-[180px]">
          <SearchableSelect
            searchTerm={citySearch}
            onSearchTermChange={handleCitySearchChange}
            options={cityOptions}
            onSelect={handleSelectCity}
            placeholder={selectedStateCode ? 'Search city...' : 'Select a state first'}
            isLoading={isLoadingCities}
            disabled={!selectedStateCode}
          />
        </div>
        <Select
          value={industryCategory}
          onValueChange={(value) => {
            setIndustryCategory(value);
            setPage(0);
          }}
        >
          <SelectTrigger className="sm:max-w-[200px]">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>All categories</SelectItem>
            {categories?.map((category) => (
              <SelectItem key={category.id} value={category.name}>
                {category.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {/* <Select
          value={isFeatured}
          onValueChange={(value) => {
            setIsFeatured(value);
            setPage(0);
          }}
        >
          <SelectTrigger className="sm:max-w-[160px]">
            <SelectValue placeholder="Featured" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>All vendors</SelectItem>
            <SelectItem value="true">Featured</SelectItem>
            <SelectItem value="false">Not featured</SelectItem>
          </SelectContent>
        </Select> */}
      </div>

      {/* Table */}
      <div className="rounded-lg border">
        <div className="overflow-x-auto">
          <table className="w-full whitespace-nowrap text-sm">
            <thead>
              <tr className="border-b bg-muted/50 text-left text-muted-foreground whitespace-nowrap">
                <th className="p-3 font-medium">Logo</th>
                <th className="p-3 font-medium">Name</th>
                <th className="p-3 font-medium">Website</th>
                <th className="p-3 font-medium">Category</th>
                <th className="p-3 font-medium">City</th>
                <th className="p-3 font-medium">State</th>
                {/* <th className="p-3 font-medium">Earn Rate</th> */}
                <th className="p-3 font-medium">Contact Person</th>
                <th className="p-3 font-medium">Business Email</th>
                <th className="p-3 font-medium">Contact Mobile</th>
                <th className="p-3 font-medium">Status</th>
                <th className="p-3 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={12} className="p-6 text-center text-muted-foreground">
                    Loading vendors...
                  </td>
                </tr>
              )}
              {!isLoading && vendors.length === 0 && (
                <tr>
                  <td colSpan={12} className="p-6 text-center text-muted-foreground">
                    No vendors found.
                  </td>
                </tr>
              )}
              {vendors.map((vendor) => (
                <tr
                  key={vendor.id}
                  onClick={() => navigate(`/vendors/${vendor.id}`)}
                  className="cursor-pointer border-b last:border-0 hover:bg-muted/30"
                >
                  <td className="p-3">
                    {vendor.logo ? (
                      <img src={vendor.logo} alt="" className="h-9 w-9 rounded-md object-cover" />
                    ) : (
                      <div className="h-9 w-9 rounded-md bg-muted" />
                    )}
                  </td>
                  <td className="p-3 font-medium">{vendor.name}</td>
                  <td className="p-3">
                    {vendor.websiteUrl && (
                      <a
                        href={
                          vendor.websiteUrl.startsWith('http')
                            ? vendor.websiteUrl
                            : `https://${vendor.websiteUrl}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        title={vendor.websiteUrl}
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center text-muted-foreground hover:text-primary"
                      >
                        {truncateMiddle(vendor.websiteUrl)}
                        <ExternalLink className="ml-1 h-3 w-3" />
                      </a>
                    )}
                  </td>
                  <td className="p-3">
                    {vendor.industryCategories && vendor.industryCategories.length > 0
                      ? vendor.industryCategories.map((c) => c.name).join(', ')
                      : vendor.industryCategory?.name || 'N/A'}
                  </td>
                  <td className="p-3">{vendor.city}</td>
                  <td className="p-3">{vendor.state}</td>
                  {/* <td className="p-3">{vendor.earnRate}%</td> */}
                  <td className="p-3">{vendor.contactPerson}</td>
                  <td className="p-3">
                    {vendor.businessEmail ? (
                      <div className="flex items-center gap-1.5" title={vendor.businessEmail}>
                        <span>{truncateMiddle(vendor.businessEmail)}</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopyEmail(vendor.businessEmail!);
                          }}
                          className="text-muted-foreground hover:text-primary transition-colors cursor-pointer bg-transparent border-none p-0.5 inline-flex items-center justify-center rounded"
                          title="Copy Email"
                        >
                          <Copy className="h-3 w-3" />
                        </button>
                      </div>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="p-3">{vendor.contactMobile.replace(/^\+91/, '')}</td>
                  <td className="p-3">
                    {vendor.status && (
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                          vendor.status.toLowerCase() === 'active'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                        }`}
                      >
                        {vendor.status}
                      </span>
                    )}
                  </td>
                  <td className="p-3" onClick={(e) => e.stopPropagation()}>
                    <VendorActions
                      vendor={vendor}
                      onEdit={() => setFormState({ open: true, vendor })}
                      onViewReferralConfig={() => setReferralModalVendor(vendor)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-col items-center justify-between gap-3 border-t p-3 sm:flex-row">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Rows per page</span>
            <Select
              value={String(pageSize)}
              onValueChange={(value) => {
                setPageSize(Number(value));
                setPage(0);
              }}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[7, 10, 25, 50].map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span>
              Page {total === 0 ? 0 : page + 1} of {totalPages} ({total} total)
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page === 0 || isFetching}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page + 1 >= totalPages || isFetching}
                onClick={() => setPage((p) => p + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <VendorFormDialog
        open={formState.open}
        vendor={formState.vendor}
        onOpenChange={(open) =>
          setFormState((prev) => ({ open, vendor: open ? prev.vendor : undefined }))
        }
      />

      <VendorReferralConfigModal
        open={Boolean(referralModalVendor)}
        vendor={referralModalVendor}
        onOpenChange={(open) => !open && setReferralModalVendor(null)}
      />
    </div>
  );
}
